import {
  createContext,
  useState,
  useEffect,
  PropsWithChildren,
  useCallback,
} from "react";
import * as auth from "@/services/auth";
import { useTranslation } from "react-i18next";
import { Account } from "@/types/account";
import Spinner from "@/components/Spinner";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { PATH_SIGN_IN } from "@/constants";
import { QueryClient } from "@tanstack/react-query";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useAccountRefs } from "../hooks/useAccountRefs";
import { useLocation } from "react-router-dom";
import { useCrossTabSync } from "../hooks/useCrossTabSync";
import { useImpersonationReturnPath } from "../hooks/useImpersonationReturnPath";
import { useSessionManagement } from "../hooks/useSessionManagement";
import { useAuthentication } from "../hooks/useAuthentication";
import { useImpersonation } from "../hooks/useImpersonation";
import { SYSTEM_ROLE_CODE } from "@/schemas/user";

interface AccountContextType {
  account: Account | null;
  onLogin: (username: string, password: string) => Promise<void>;
  onLogout: () => void;
  isSignedIn: () => boolean;
  impersonateCognitoId: string | null;
  onImpersonate: (cognitoId: string) => Promise<void>;
  onStopImpersonation: () => Promise<void>;
  isContractor: () => boolean;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

interface AccountProviderProps extends PropsWithChildren {
  queryClient?: QueryClient;
}

export const AccountProvider = ({
  children,
  queryClient,
}: AccountProviderProps) => {
  const { i18n } = useTranslation();
  const location = useLocation();

  const [impersonateCognitoId, setImpersonateCognitoId] = useLocalStorage<
    string | null
  >("impersonateCognitoId", null);

  const [impersonationReturnPath, setImpersonationReturnPath] =
    useImpersonationReturnPath();

  const {
    toastRef,
    tRef,
    navigateRef,
    queryClientRef,
    impersonateCognitoIdRef,
  } = useAccountRefs(queryClient, impersonateCognitoId);

  const [account, setAccount] = useState<Account | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<boolean>(false);
  const [loadingImpersonation, setLoadingImpersonation] = useState(false);
  const [impersonationMessage, setImpersonationMessage] = useState<string>("");

  const isContractor = useCallback(() => {
    return account?.system_role_code === SYSTEM_ROLE_CODE.CONTRACTOR;
  }, [account]);

  const broadcastChannelRef = useCrossTabSync({
    setImpersonateCognitoId,
    setLoadingImpersonation,
    setImpersonationMessage,
    setAccount,
    setImpersonationReturnPath,
    impersonationReturnPath,
    locationPathname: location.pathname,
    queryClientRef,
    navigateRef,
    tRef,
    toastRef,
  });

  const { destroySession, initializeSession } = useSessionManagement({
    impersonateCognitoIdRef,
    setAccount,
  });

  const { handleLogin, isSignedIn, handleLogout } = useAuthentication({
    destroySession,
    initializeSession,
    setImpersonateCognitoId,
    setImpersonationReturnPath,
  });

  const { handleImpersonate, handleStopImpersonation } = useImpersonation({
    broadcastChannelRef,
    setImpersonateCognitoId,
    setImpersonationReturnPath,
    impersonationReturnPath,
    setLoadingImpersonation,
    setImpersonationMessage,
    setAccount,
    locationPathname: location.pathname,
    queryClientRef,
    navigateRef,
    tRef,
    toastRef,
  });

  // ============================================================================
  // Effects
  // ============================================================================
  useEffect(() => {
    if (!auth.isSignedIn()) {
      setLoading(false);
      return;
    }
    initializeSession()
      .catch((err) => {
        console.error(err);
        if (err.code === "NotAuthorizedException") {
          toastRef.current({
            title: tRef.current("auth.errors.refreshTokenExpired"),
            description: tRef.current("auth.errors.pleaseSignInAgain"),
            variant: "destructive",
          });
          destroySession();
          navigateRef.current(PATH_SIGN_IN);
        } else {
          setError(true);
          destroySession();
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [initializeSession, destroySession, toastRef, tRef, navigateRef]);

  // Update language when account changes
  useEffect(() => {
    if (account && account.preferred_language_iso_code) {
      i18n.changeLanguage(account.preferred_language_iso_code);
    }
  }, [i18n, account]);

  if (loading) {
    return <Spinner className="h-screen w-screen" size="lg" />;
  }

  if (loadingImpersonation) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-screen gap-4">
        <Spinner size="lg" />
        <div className="text-lg text-gray-600">{impersonationMessage}</div>
      </div>
    );
  }

  return (
    <AccountContext.Provider
      value={{
        account,
        onLogin: handleLogin,
        onLogout: handleLogout,
        isSignedIn,
        impersonateCognitoId,
        onImpersonate: handleImpersonate,
        onStopImpersonation: handleStopImpersonation,
        isContractor,
      }}
    >
      {error && (
        <div className="p-2">
          <Alert variant="destructive">
            <AlertTitle>{tRef.current("auth.errors.title")}</AlertTitle>
            <AlertDescription>
              {tRef.current("auth.errors.unknownError")}
            </AlertDescription>
          </Alert>
        </div>
      )}
      {!error && children}
    </AccountContext.Provider>
  );
};

export default AccountContext;
